import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, ShieldAlert } from "lucide-react";
import { SITE } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "General",
  "Account & login",
  "Nova Premium & purchases",
  "Bank-statement analysis",
  "Bug report",
  "Privacy request",
];

export const ContactForm = ({ compact = false }) => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", category: "General", company: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (form.name.trim().length < 2) err.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Please enter a valid email address.";
    if (form.subject.trim().length < 2) err.subject = "Please enter a subject.";
    if (form.message.trim().length < 10) err.message = "Please add a little more detail (10+ characters).";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", subject: "", message: "", category: "General", company: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please email us directly.";
      toast.error(typeof msg === "string" ? msg : "Please try again later.");
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/35 focus-visible:border-nova-cyan/50";

  if (done) {
    return (
      <div className="glass rounded-2xl p-8 text-center" data-testid="contact-success">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-nova-emerald/15 text-nova-emerald">
          <Send size={20} />
        </div>
        <h3 className="font-heading text-xl font-bold text-white">Message received</h3>
        <p className="mt-2 text-sm text-white/60">
          Thanks for reaching out. We'll reply to your email as soon as we can.
        </p>
        <button className="btn-ghost mt-6" onClick={() => setDone(false)} data-testid="contact-send-another">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="glass rounded-2xl p-6 sm:p-8" noValidate data-testid="contact-form">
      {/* Honeypot: hidden from users, catches bots */}
      <input
        type="text"
        name="company"
        value={form.company}
        onChange={set("company")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />

      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <label htmlFor="cf-name" className="mb-1.5 block text-sm font-medium text-white/80">Name</label>
          <input id="cf-name" className={field} value={form.name} onChange={set("name")} data-testid="contact-name" autoComplete="name" />
          {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="cf-email" className="mb-1.5 block text-sm font-medium text-white/80">Email</label>
          <input id="cf-email" type="email" className={field} value={form.email} onChange={set("email")} data-testid="contact-email" autoComplete="email" />
          {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-category" className="mb-1.5 block text-sm font-medium text-white/80">Category</label>
          <select id="cf-category" className={field} value={form.category} onChange={set("category")} data-testid="contact-category">
            {CATEGORIES.map((c) => (
              <option key={c} value={c} className="bg-nova-surface">{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="cf-subject" className="mb-1.5 block text-sm font-medium text-white/80">Subject</label>
          <input id="cf-subject" className={field} value={form.subject} onChange={set("subject")} data-testid="contact-subject" />
          {errors.subject && <p className="mt-1 text-xs text-red-400">{errors.subject}</p>}
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="cf-message" className="mb-1.5 block text-sm font-medium text-white/80">Message</label>
        <textarea id="cf-message" rows={compact ? 4 : 6} className={field} value={form.message} onChange={set("message")} data-testid="contact-message" />
        {errors.message && <p className="mt-1 text-xs text-red-400">{errors.message}</p>}
      </div>

      <p className="mt-3 flex items-start gap-2 text-xs text-white/40">
        <ShieldAlert size={14} className="mt-0.5 shrink-0 text-white/40" />
        For your security, never include passwords, PINs, payment-card numbers or bank statements in this form.
      </p>

      <button type="submit" className="btn-primary mt-5 w-full sm:w-auto" disabled={sending} data-testid="contact-submit">
        {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {sending ? "Sending…" : "Send message"}
      </button>

      <p className="mt-4 text-xs text-white/40">
        Prefer email? Write to{" "}
        <a href={`mailto:${SITE.supportEmail}`} className="text-nova-cyan hover:underline">{SITE.supportEmail}</a>.
      </p>
    </form>
  );
};

export default ContactForm;
